import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { Link, useNavigate } from 'react-router';

import { Container, Item } from 'Components/Grid';
import PlatformSelectVisitReason from './SearchAndCreation/PlatformSelectVisitReason';
import { useAppSelector, useAppDispatch, assertCapabilities } from 'Store/index';
import ContentWrapper from 'Components/ContentWrapper';
import TabContent from 'Components/TabContent';
import StatisticsTab from './Statistics';
import SearchAndCreation from './SearchAndCreation';
import { createVisitAndModifyAuth, jwtModify, patientSearch, patientSelect } from './fetchers';
import ActionButtonRounded from 'Components/ActionButtonRounded';
import Icon from 'Components/_NewElements/Icon';
import PreviousPatients from './SearchAndCreation/PreviousPatients';
import Messages from './MessageCentral/Messages';
import { actions } from 'Store/patientsearch';
import { actions as sessionActions } from 'Store/session';
import { ActionableErrorContext, Loggable, makeLog } from 'Utility/logger';
import { localStorageSave } from 'Utility/localStorage';
import { ICapabilityContextProps, withCapabilities } from 'Containers/CapabilityHandler';
import PlatformCapabilities from '../../config/capabilities';
import { TCMReactState, useConnections } from '../../net/ConnectionManagerReactAdapter';

type TTabNames = 'patientSearch' | 'extendedSearch' | 'previousPatients' | 'statistics' | 'messaging';

const MainLander = ({ prevPatient, activeTab = 'patientSearch', capabilityGroups = {} }: IMainLander) => {
  const settings = useAppSelector(s => s.settings);
  const session = useAppSelector(s => s.session);
  const allPlatforms = useAppSelector((s: IState) => s.session.data?.platforms) as Platform[] | undefined;
  const { patientid, visitid, patientfirstnames, patientlastnames, patientssn, orgid } = session.data || {};
  const connections: TCMReactState = useConnections();

  const [tabState, setTabState] = React.useState<TTabNames>(activeTab);

  let tabList: Array<{
    name: TTabNames;
    enabled: boolean;
  }> = [
    { name: 'patientSearch', enabled: true },
    { name: 'extendedSearch', enabled: true },
    { name: 'previousPatients', enabled: true },
    { name: 'messaging', enabled: false },
    //{ name: 'groupMessages', enabled: false },
    { name: 'statistics', enabled: false },
  ];

  const dispach = useAppDispatch();
  const selectTab = (tab: typeof tabState) => {
    actions.clearSearchResults(dispach);

    setTabState(tab);
  };

  const powerBiCapability = settings.orgSettings?.powerbi;
  const possibleStatisticsPlatforms: Platform[] = [];
  // Find out if and for which platforms a powerbi report id exists
  if (powerBiCapability) {
    const piPlatforms = Object.keys(powerBiCapability) as Platform[];
    piPlatforms.forEach(pp => {
      if (powerBiCapability[pp]?.reportId) {
        possibleStatisticsPlatforms.push(pp);
      }
    });
    // Add statistics to tabs if available
    if (possibleStatisticsPlatforms.length > 0)
      tabList = tabList.map(t => (t.name === 'statistics' ? { ...t, enabled: true } : t));
  }

  const runStarted = React.useRef<boolean>(false);

  const dispatch = useAppDispatch();
  const navigation = useNavigate();

  React.useEffect(() => {
    if (prevPatient || runStarted.current) return;
    if (connections.inflight_fetch_count_api > 0 || connections.inflight_fetch_count_auth > 0) return;

    const runFetches = async () => {
      let platformToOpen: Platform | undefined;
      if (orgid && !patientid && patientssn) {
        const patSearch = await patientSearch(orgid, patientssn);
        if (patSearch && patSearch[0]) {
          const patSelectRes = await patientSelect(patSearch[0].id);
          if (patSelectRes && patSelectRes.status === 200) {
            const text = await patSelectRes.text();
            await jwtModify(text);

            const lifeCareSyncCapability = assertCapabilities(
              [PlatformCapabilities.LIFECARE_DIAGNOSIS_SYNC],
              capabilityGroups,
            );

            const availablePlatforms = await sessionActions
              .preferredPlatforms(dispatch, lifeCareSyncCapability)
              .then(platforms => {
                const filteredAvailable = platforms ? platforms.filter(p => (allPlatforms || []).includes(p)) : [];
                return filteredAvailable;
              })
              .catch(() => {
                return [] as Platform[];
              });

            platformToOpen = availablePlatforms?.[0] ?? allPlatforms?.[0];
            await createVisitAndModifyAuth('contextLogin', null, platformToOpen);
          }
        }
      }

      if (!session.data) dispatch(sessionActions.loadSession());
      if (platformToOpen) {
        localStorageSave('platform', platformToOpen);
        dispatch(sessionActions.setPlatform(platformToOpen));
      }
      dispatch(sessionActions.updateSession());
      navigation('/');
    };

    runStarted.current = true;
    runFetches().catch(err => {
      makeLog(
        Loggable.BugActionable,
        new Error('runFetches failed within useEffect on MainLander', { cause: err }),
        new ActionableErrorContext(null, 'runFetches failed within useEffect on MainLander'),
      );
    });
  }, [connections, prevPatient]);

  const messagingSettings = useAppSelector(s => s.settings.orgSettings.messagingSettings);
  const messageCenterCapa = messagingSettings && Object.keys(messagingSettings).length > 0;
  if (messageCenterCapa) {
    tabList = tabList.map(t => (t.name === 'messaging' ? { ...t, enabled: true } : t));
    //tabList = tabList.map((t) => (t.name === 'groupMessages' ? { ...t, enabled: true } : t));
  }

  const selectedPatientData = {
    patientid: patientid,
    patientfirstnames: patientfirstnames,
    patientlastnames: patientlastnames,
    patientssn: patientssn,
  };

  const availableTabs = tabList.filter(t => t.enabled);
  const moveToGroupMessages = () => selectTab('messaging');

  return (
    <div style={{ margin: '2rem 0 1rem 0' }}>
      {selectedPatientData.patientid && !visitid ? (
        /*
         * Display visit reason selection if:
         * * There is no visit id and:
         * * The patient id is included in the JWT (session) data or:
         * * Patient search produces a result with the given ssn (from JWT, would imply context login, but some kind of error with data)
         */
        <Container justifyContent="center" style={{ width: '100%', margin: '2.2rem 0' }}>
          <Item>
            <PlatformSelectVisitReason patient={{ ...selectedPatientData, patientid: selectedPatientData.patientid }} />
          </Item>
        </Container>
      ) : (
        /* No patient has been selected at this point, or the page was accessed when some patient page has already been opened */
        <ContentWrapper maxWidth="md">
          <>
            {prevPatient && (
              /* Show back button when opening search page when patient already opened */
              <div style={{ margin: '1rem 0 2rem 0' }}>
                <Link to="/">
                  <ActionButtonRounded
                    width={'auto'}
                    height={3.5}
                    fontSize={15}
                    text={
                      <Container alignItems="center">
                        <Item style={{ marginLeft: '-1rem' }}>
                          <Icon icon="leftMoveArrow" color={'white'} />
                        </Item>
                        <Item>{<FormattedMessage id="general.back" />}</Item>
                      </Container>
                    }
                    filled
                  />
                </Link>
              </div>
            )}
            <TabContent
              indexSelectionTools={{
                index: availableTabs.map(t => t.name).indexOf(tabState),

                change: (i: number) => selectTab(availableTabs[i].name),
              }}
              disableScaling
              halfScreenSmallerTabNames
            >
              {Object.values(availableTabs).map((s, i) => ({
                key: i,
                id: `$tab${i}`,
                title: (
                  <div style={{ textTransform: 'uppercase' }}>
                    <FormattedMessage id={`general.${s.name}`} />
                  </div>
                ),
                content: (
                  <div style={{ padding: '1rem 2rem 2rem 2rem' }}>
                    {
                      {
                        patientSearch: <SearchAndCreation extended={false} />,
                        extendedSearch: <SearchAndCreation extended={true} moveToGroupMessages={moveToGroupMessages} />,
                        previousPatients: <PreviousPatients />,
                        messaging: <Messages />,
                        //groupMessages: <GroupMessages />,
                        statistics: <StatisticsTab possibleStatisticsPlatforms={possibleStatisticsPlatforms} />,
                      }[tabState]
                    }
                  </div>
                ),
                count: i,
              }))}
            </TabContent>
          </>
        </ContentWrapper>
      )}
    </div>
  );
};

interface IMainLander extends ICapabilityContextProps {
  /** Whether there is an active patient and back to patient data button should be shown */
  prevPatient?: boolean;
  /** Which tab should be opened when opening the lander */
  activeTab?: TTabNames;
}

export default withCapabilities(MainLander);
